import ReferenceChart from "@/components/ReferenceChart";

const ROWS = [
  // Core parameters
  { key: "km", big: "Kₘ", small: "M", name: "Michaelis Constant", group: "Core parameter", essential: true, tag: "[S] at ½Vmax", note: "Inverse measure of substrate AFFINITY. LOW Km = high affinity, tight binding." },
  { key: "vmax", big: "Vmax", small: "M/s", name: "Maximum Velocity", group: "Core parameter", essential: true, tag: "at [S]≫Km", note: "Rate when enzyme is SATURATED. Proportional to [E]total." },
  { key: "kcat", big: "kcat", small: "1/s", name: "Turnover Number", group: "Core parameter", tag: "Vmax/[E]", note: "Reactions per enzyme per second at saturation. Efficiency = kcat/Km." },
  // Inhibitor types — MCAT high-yield
  { key: "competitive", big: "I → active site", small: "vs [S]", name: "Competitive Inhibitor", group: "Inhibitor", essential: true, tag: "↑ Km · Vmax same", note: "Binds ACTIVE SITE. Beaten by more substrate. Lineweaver: same y-intercept, diff x." },
  { key: "noncompetitive", big: "I → allosteric", small: "any [S]", name: "Noncompetitive Inhibitor", group: "Inhibitor", essential: true, tag: "Km same · ↓ Vmax", note: "Binds ALLOSTERIC site regardless of substrate. Same Km, lower Vmax." },
  { key: "uncompetitive", big: "I → ES only", small: "req. [ES]", name: "Uncompetitive Inhibitor", group: "Inhibitor", essential: true, tag: "↓ Km · ↓ Vmax", note: "Binds ONLY the ES complex. Both Km and Vmax decrease proportionally." },
  { key: "mixed", big: "I → E and ES", small: "differ affinity", name: "Mixed Inhibitor", group: "Inhibitor", tag: "Km ↑ or ↓ · ↓ Vmax", note: "Binds E and ES with different affinity. Vmax always drops; Km may shift either way." },
  // Regulation
  { key: "allosteric-pos", big: "K↓ / V↑", small: "positive", name: "Allosteric Activator", group: "Regulation", tag: "↑ affinity", note: "Binds allosteric site to stabilize R (relaxed) state. Cooperativity ↑." },
  { key: "allosteric-neg", big: "K↑ / V↓", small: "negative", name: "Allosteric Inhibitor", group: "Regulation", tag: "↓ affinity", note: "Stabilizes T (tense) state. Classic feedback inhibitor (product inhibits pathway)." },
  { key: "cooperativity", big: "Hill n>1", small: "sigmoidal", name: "Cooperativity", group: "Regulation", tag: "S-shape curve", note: "Multiple subunits (Hb, PFK). Sigmoidal, not hyperbolic. Hill coefficient > 1." },
  { key: "covalent", big: "P/PO₄", small: "reversible", name: "Covalent Modification", group: "Regulation", tag: "phospho / de-", note: "Kinases add PO₄, phosphatases remove. Fast on/off switching (glycogen metabolism)." },
  // Curve behavior
  { key: "mm-eq", big: "V = Vmax[S] / (Km + [S])", small: "MM eqn", name: "Michaelis-Menten Equation", group: "Curve", essential: true, tag: "hyperbolic", note: "Assumes steady-state [ES]. At [S] = Km, V = Vmax/2. At [S]≫Km, V ≈ Vmax." },
  { key: "lineweaver", big: "1/V vs 1/[S]", small: "double reciprocal", name: "Lineweaver-Burk Plot", group: "Curve", essential: true, tag: "linear", note: "y-int = 1/Vmax · x-int = -1/Km. Straight line — easier to read inhibitor effects." },
];

const GROUP_COLORS = {
  "Core parameter": { bg: "#f3ede1", text: "#7a5b2a", pill: "#c98a3b" },
  Inhibitor: { bg: "#f6e5e5", text: "#7a3a3a", pill: "#c95e5e" },
  Regulation: { bg: "#e8f1e5", text: "#3d5a2f", pill: "#7ba55c" },
  Curve: { bg: "#e5edf5", text: "#2d4a6f", pill: "#4f7d8a" },
};

export default function EnzymeKineticsChart() {
  return (
    <ReferenceChart
      title="Enzyme Kinetics Reference"
      subtitle="Km, Vmax, and the four inhibitor types in one glance. Star ★ = high-yield MCAT concept."
      sourceChart="enzyme-kinetics"
      rows={ROWS}
      groupColors={GROUP_COLORS}
      deckMapper={(r) => ({
        front: `${r.name}${r.tag ? ` — ${r.tag}` : ""}`,
        back: `${r.group}. ${r.note}`,
        meta: { key: r.key, group: r.group },
      })}
      footer={
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <b>Inhibitor cheat sheet:</b><br />
            • Competitive → ↑ Km, Vmax UNCHANGED<br />
            • Noncompetitive → Km UNCHANGED, ↓ Vmax<br />
            • Uncompetitive → ↓ Km AND ↓ Vmax (both drop)<br />
            • Mixed → Km shifts, Vmax always drops
          </div>
          <div>
            <b>Lineweaver-Burk reading:</b> Comp inhibitors share y-intercept
            (same Vmax). Noncomp inhibitors share x-intercept (same Km).
            Uncomp inhibitors give PARALLEL lines.
          </div>
        </div>
      }
    />
  );
}
